import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { toISO } from '../utils/dates';

// ── Toast hook ────────────────────────────────────────────────────────────────
export function useToast() {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);
  return { toast, show };
}

// ── Cache backed by sessionStorage (survives page refresh, clears on tab close) ──
const CACHE_TTL = 90_000; // 90 seconds
const CACHE_PFX = 'sf_';
function getCache(key) {
  if (!key) return undefined;
  try {
    const raw = sessionStorage.getItem(CACHE_PFX + key);
    if (!raw) return undefined;
    const e = JSON.parse(raw);
    return (Date.now() - e.ts < CACHE_TTL) ? e.data : undefined;
  } catch { return undefined; }
}
function setCache(key, data) {
  if (!key) return;
  try { sessionStorage.setItem(CACHE_PFX + key, JSON.stringify({ data, ts: Date.now() })); }
  catch {} // ignore quota errors
}

// ── Departments ───────────────────────────────────────────────────────────────
export function useDepartments() {
  const { org } = useAuth();
  const cKey = org ? `depts_${org.id}` : null;
  const [departments, setDepartments] = useState(() => getCache(cKey) ?? []);
  const [loading, setLoading]         = useState(() => getCache(cKey) === undefined);

  const load = useCallback(async (skipCache = false) => {
    if (!org) { setLoading(false); return; }
    const key = `depts_${org.id}`;
    if (!skipCache) {
      const cached = getCache(key);
      if (cached !== undefined) { setDepartments(cached); setLoading(false); }
    }
    try {
      const { data } = await supabase
        .from('departments').select('*')
        .eq('org_id', org.id)
        .order('display_order').order('name');
      const result = data || [];
      setCache(key, result); setDepartments(result);
    } finally { setLoading(false); }
  }, [org]);

  useEffect(() => { load(); }, [load]);
  return { departments, loading, reload: useCallback(() => load(true), [load]) };
}

// ── Employees ─────────────────────────────────────────────────────────────────
export function useEmployees() {
  const { org } = useAuth();
  const cKey = org ? `emp_${org.id}` : null;
  const [employees, setEmployees] = useState(() => getCache(cKey) ?? []);
  const [loading, setLoading]     = useState(() => getCache(cKey) === undefined);

  const load = useCallback(async (skipCache = false) => {
    if (!org) { setLoading(false); return; }
    const key = `emp_${org.id}`;
    if (!skipCache) {
      const cached = getCache(key);
      if (cached !== undefined) { setEmployees(cached); setLoading(false); }
    }
    try {
      const { data } = await supabase
        .from('profiles').select('*')
        .eq('org_id', org.id).eq('role', 'employee').eq('is_active', true).order('name');
      const result = data || [];
      setCache(key, result); setEmployees(result);
    } finally { setLoading(false); }
  }, [org]);

  useEffect(() => { load(); }, [load]);
  return { employees, loading, reload: useCallback(() => load(true), [load]) };
}

// ── All team members (employees + managers, excluding owners) ─────────────────
export function useTeamMembers() {
  const { org } = useAuth();
  const cKey = org ? `team_${org.id}` : null;
  const [members, setMembers] = useState(() => getCache(cKey) ?? []);
  const [loading, setLoading] = useState(() => getCache(cKey) === undefined);

  const load = useCallback(async (skipCache = false) => {
    if (!org) { setLoading(false); return; }
    const key = `team_${org.id}`;
    if (!skipCache) {
      const cached = getCache(key);
      if (cached !== undefined) { setMembers(cached); setLoading(false); }
    }
    try {
      const { data } = await supabase
        .from('profiles').select('*')
        .eq('org_id', org.id).neq('role', 'owner').eq('is_active', true).order('name');
      const result = data || [];
      setCache(key, result); setMembers(result);
    } finally { setLoading(false); }
  }, [org]);

  useEffect(() => { load(); }, [load]);
  return { members, loading, reload: useCallback(() => load(true), [load]) };
}

// ── Direct reports (people whose reports_to = current user) ──────────────────
export function useDirectReports() {
  const { org, profile } = useAuth();
  const cKey = org && profile ? `dr_${org.id}_${profile.id}` : null;
  const [reports, setReports] = useState(() => getCache(cKey) ?? []);
  const [loading, setLoading] = useState(() => getCache(cKey) === undefined);

  const load = useCallback(async (skipCache = false) => {
    if (!org || !profile) { setLoading(false); return; }
    const key = `dr_${org.id}_${profile.id}`;
    if (!skipCache) {
      const cached = getCache(key);
      if (cached !== undefined) { setReports(cached); setLoading(false); }
    }
    try {
      const { data } = await supabase
        .from('profiles').select('*')
        .eq('org_id', org.id).eq('reports_to', profile.id).eq('is_active', true).order('name');
      const result = data || [];
      setCache(key, result); setReports(result);
    } finally { setLoading(false); }
  }, [org, profile]);

  useEffect(() => { load(); }, [load]);
  return { reports, loading, reload: useCallback(() => load(true), [load]) };
}

// ── Shifts for a given week ───────────────────────────────────────────────────
export function useShifts(weekStart, weekEnd) {
  const { org } = useAuth();
  const cKey = org && weekStart ? `shifts_${org.id}_${weekStart}_${weekEnd}` : null;
  const [shifts, setShifts]   = useState(() => getCache(cKey) ?? []);
  const [loading, setLoading] = useState(() => getCache(cKey) === undefined);

  const load = useCallback(async (skipCache = false) => {
    if (!org || !weekStart) { setLoading(false); return; }
    const key = `shifts_${org.id}_${weekStart}_${weekEnd}`;
    if (!skipCache) {
      const cached = getCache(key);
      if (cached !== undefined) { setShifts(cached); setLoading(false); }
    }
    try {
      const { data } = await supabase
        .from('shifts')
        .select('*, employee:profiles(id,name,avatar_color,avatar_text_color,position,department)')
        .eq('org_id', org.id)
        .gte('shift_date', weekStart).lte('shift_date', weekEnd)
        .order('shift_date').order('start_time');
      const result = data || [];
      setCache(key, result); setShifts(result);
    } finally { setLoading(false); }
  }, [org, weekStart, weekEnd]);

  useEffect(() => { load(); }, [load]);
  return { shifts, loading, reload: useCallback(() => load(true), [load]) };
}

// ── My shifts (employee) ──────────────────────────────────────────────────────
export function useMyShifts(weekStart, weekEnd) {
  const { profile } = useAuth();
  const cKey = profile ? `myShifts_${profile.id}_${weekStart}_${weekEnd}` : null;
  const [shifts, setShifts]   = useState(() => getCache(cKey) ?? []);
  const [loading, setLoading] = useState(() => getCache(cKey) === undefined);

  const load = useCallback(async (skipCache = false) => {
    if (!profile) { setLoading(false); return; }
    const key = `myShifts_${profile.id}_${weekStart}_${weekEnd}`;
    if (!skipCache) {
      const cached = getCache(key);
      if (cached !== undefined) { setShifts(cached); setLoading(false); }
    }
    try {
      let query = supabase.from('shifts').select('*').eq('employee_id', profile.id).order('shift_date');
      if (weekStart) query = query.gte('shift_date', weekStart);
      if (weekEnd)   query = query.lte('shift_date', weekEnd);
      const { data } = await query;
      const result = data || [];
      setCache(key, result); setShifts(result);
    } finally { setLoading(false); }
  }, [profile, weekStart, weekEnd]);

  useEffect(() => { load(); }, [load]);
  return { shifts, loading, reload: useCallback(() => load(true), [load]) };
}

// ── Available shifts ──────────────────────────────────────────────────────────
export function useAvailableShifts() {
  const { org, profile } = useAuth();
  const cKey = org ? `avail_${org.id}_${profile?.id}` : null;
  const [available, setAvailable] = useState(() => getCache(cKey) ?? []);
  const [loading, setLoading]     = useState(() => getCache(cKey) === undefined);

  const load = useCallback(async (skipCache = false) => {
    if (!org) { setLoading(false); return; }
    const key = `avail_${org.id}_${profile?.id}`;
    if (!skipCache) {
      const cached = getCache(key);
      if (cached !== undefined) { setAvailable(cached); setLoading(false); }
    }
    try {
      const { data } = await supabase
        .from('available_shifts')
        .select(`*, shift:shifts(*), posted_by_profile:profiles!available_shifts_posted_by_fkey(id,name,avatar_color,avatar_text_color)`)
        .eq('org_id', org.id).eq('is_open', true).neq('posted_by', profile?.id)
        .order('created_at', { ascending: false });
      const result = data || [];
      setCache(key, result); setAvailable(result);
    } finally { setLoading(false); }
  }, [org, profile]);

  useEffect(() => { load(); }, [load]);
  return { available, loading, reload: useCallback(() => load(true), [load]) };
}

// ── Shift requests ────────────────────────────────────────────────────────────
export function useShiftRequests() {
  const { org } = useAuth();
  const cKey = org ? `shiftReqs_${org.id}` : null;
  const [requests, setRequests] = useState(() => getCache(cKey) ?? []);
  const [loading, setLoading]   = useState(() => getCache(cKey) === undefined);

  const load = useCallback(async (skipCache = false) => {
    if (!org) { setLoading(false); return; }
    const key = `shiftReqs_${org.id}`;
    if (!skipCache) {
      const cached = getCache(key);
      if (cached !== undefined) { setRequests(cached); setLoading(false); }
    }
    try {
      const { data } = await supabase
        .from('shift_requests')
        .select(`*, requester:profiles!shift_requests_requester_id_fkey(id,name,avatar_color,avatar_text_color,reports_to), available_shift:available_shifts(*, shift:shifts(*)), offered_shift:shifts!offered_shift_id(id,position,shift_date,start_time,end_time,employee_id,employee:profiles(id,name))`)
        .eq('org_id', org.id).order('created_at', { ascending: false });
      const result = data || [];
      setCache(key, result); setRequests(result);
    } finally { setLoading(false); }
  }, [org]);

  useEffect(() => { load(); }, [load]);
  return { requests, loading, reload: useCallback(() => load(true), [load]) };
}

// ── My requests (employee) ────────────────────────────────────────────────────
export function useMyRequests() {
  const { profile } = useAuth();
  const cKey = profile ? `myReqs_${profile.id}` : null;
  const [requests, setRequests] = useState(() => getCache(cKey) ?? []);
  const [loading, setLoading]   = useState(() => getCache(cKey) === undefined);

  const load = useCallback(async (skipCache = false) => {
    if (!profile) { setLoading(false); return; }
    const key = `myReqs_${profile.id}`;
    if (!skipCache) {
      const cached = getCache(key);
      if (cached !== undefined) { setRequests(cached); setLoading(false); }
    }
    try {
      const { data } = await supabase
        .from('shift_requests')
        .select(`*, available_shift:available_shifts(*, shift:shifts(*))`)
        .eq('requester_id', profile.id).order('created_at', { ascending: false });
      const result = data || [];
      setCache(key, result); setRequests(result);
    } finally { setLoading(false); }
  }, [profile]);

  useEffect(() => { load(); }, [load]);
  return { requests, loading, reload: useCallback(() => load(true), [load]) };
}

// ── Notifications (real-time) ─────────────────────────────────────────────────
export function useNotifications() {
  const { profile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const channelRef = useRef(null);

  const load = useCallback(async () => {
    if (!profile) return;
    const { data } = await supabase
      .from('notifications').select('*')
      .eq('user_id', profile.id).order('created_at', { ascending: false }).limit(50);
    setNotifications(data || []);
  }, [profile]);

  useEffect(() => {
    if (!profile) return;
    if (channelRef.current) { supabase.removeChannel(channelRef.current); channelRef.current = null; }
    load();
    const channelName = `notif-${profile.id}-${Math.random().toString(36).slice(2)}`;
    const channel = supabase.channel(channelName);
    channel
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${profile.id}` },
        (payload) => { setNotifications(prev => [payload.new, ...prev]); })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user_id=eq.${profile.id}` },
        (payload) => { setNotifications(prev => prev.map(n => n.id === payload.new.id ? payload.new : n)); });
    channel.subscribe();
    channelRef.current = channel;
    return () => { if (channelRef.current) { supabase.removeChannel(channelRef.current); channelRef.current = null; } };
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const markAllRead = useCallback(async () => {
    if (!profile) return;
    await supabase.from('notifications').update({ read: true }).eq('user_id', profile.id).eq('read', false);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, [profile]);

  const unreadCount = notifications.filter(n => !n.read).length;
  return { notifications, unreadCount, reload: load, markAllRead };
}

// ── Schedule ──────────────────────────────────────────────────────────────────
export function useSchedule(weekStartISO) {
  const { org } = useAuth();
  const cKey = org && weekStartISO ? `sched_${org.id}_${weekStartISO}` : null;
  const [schedule, setSchedule] = useState(() => {
    const c = getCache(cKey); return c !== undefined ? c : null;
  });

  const load = useCallback(async (skipCache = false) => {
    if (!org || !weekStartISO) return;
    const key = `sched_${org.id}_${weekStartISO}`;
    if (!skipCache) {
      const cached = getCache(key);
      if (cached !== undefined) { setSchedule(cached); }
    }
    const { data } = await supabase
      .from('schedules').select('*')
      .eq('org_id', org.id).eq('week_start', weekStartISO).maybeSingle();
    setCache(key, data ?? null); setSchedule(data ?? null);
  }, [org, weekStartISO]);

  useEffect(() => { load(); }, [load]);

  const ensureSchedule = useCallback(async (managerId) => {
    if (schedule) return schedule;
    const { data } = await supabase
      .from('schedules')
      .insert({ org_id: org.id, manager_id: managerId, week_start: weekStartISO, published: false })
      .select().single();
    const key = `sched_${org.id}_${weekStartISO}`;
    setCache(key, data); setSchedule(data);
    return data;
  }, [org, weekStartISO, schedule]);

  const publish = useCallback(async (employeeIds) => {
    if (!schedule) return;
    await supabase.from('schedules').update({ published: true, published_at: new Date().toISOString() }).eq('id', schedule.id);
    const notifs = employeeIds.map(uid => ({ org_id: org.id, user_id: uid, text: 'Your schedule has been published. Check your shifts!' }));
    if (notifs.length > 0) await supabase.from('notifications').insert(notifs);
    const updated = { ...schedule, published: true };
    const key = `sched_${org.id}_${weekStartISO}`;
    setCache(key, updated); setSchedule(updated);
  }, [schedule, org, weekStartISO]);

  return { schedule, reload: useCallback(() => load(true), [load]), ensureSchedule, publish };
}

// ── Invites ───────────────────────────────────────────────────────────────────
export function useInvites() {
  const { org, profile } = useAuth();
  const cKey = org ? `invites_${org.id}` : null;
  const [invites, setInvites] = useState(() => getCache(cKey) ?? []);

  const load = useCallback(async (skipCache = false) => {
    if (!org) return;
    const key = `invites_${org.id}`;
    if (!skipCache) {
      const cached = getCache(key);
      if (cached !== undefined) { setInvites(cached); }
    }
    const { data } = await supabase
      .from('invites').select('*')
      .eq('org_id', org.id).eq('accepted', false)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });
    const result = data || [];
    setCache(key, result); setInvites(result);
  }, [org]);

  useEffect(() => { load(); }, [load]);

  const sendInvite = useCallback(async ({ name, email, position, department_ids = [], role = 'employee', reports_to }) => {
    const now = new Date().toISOString();

    if (role === 'employee') {
      const [{ count: active }, { count: pending }] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true })
          .eq('org_id', org.id).eq('role', 'employee').eq('is_active', true),
        supabase.from('invites').select('*', { count: 'exact', head: true })
          .eq('org_id', org.id).eq('role', 'employee').eq('accepted', false).gt('expires_at', now),
      ]);
      const total = (active ?? 0) + (pending ?? 0);
      if (total >= org.max_employees) {
        const planName = org.plan.charAt(0).toUpperCase() + org.plan.slice(1);
        throw new Error(`Your ${planName} plan allows up to ${org.max_employees} employees. Upgrade to add more.`);
      }
    }

    if (role === 'manager') {
      const managerLimit = org.plan === 'trial' ? 1 : org.plan === 'starter' ? 2 : null;
      if (managerLimit !== null) {
        const [{ count: active }, { count: pending }] = await Promise.all([
          supabase.from('profiles').select('*', { count: 'exact', head: true })
            .eq('org_id', org.id).in('role', ['manager', 'owner']).eq('is_active', true),
          supabase.from('invites').select('*', { count: 'exact', head: true })
            .eq('org_id', org.id).eq('role', 'manager').eq('accepted', false).gt('expires_at', now),
        ]);
        if ((active ?? 0) + (pending ?? 0) >= managerLimit) {
          const planName = org.plan.charAt(0).toUpperCase() + org.plan.slice(1);
          throw new Error(`Your ${planName} plan allows up to ${managerLimit} manager${managerLimit > 1 ? 's' : ''}. Upgrade to add more.`);
        }
      }
    }

    const { data: existingMember } = await supabase
      .from('profiles').select('id').eq('org_id', org.id).eq('email', email).maybeSingle();
    if (existingMember) throw new Error(`${email} is already a member of this organization.`);

    const { data: pendingInvite } = await supabase
      .from('invites').select('id')
      .eq('org_id', org.id).eq('email', email)
      .eq('accepted', false).gt('expires_at', new Date().toISOString())
      .maybeSingle();
    if (pendingInvite) throw new Error(`${email} already has a pending invite.`);

    const { data, error } = await supabase
      .from('invites')
      .insert({ org_id: org.id, name, email, position, department_ids, invited_by: profile.id, role, ...(reports_to ? { reports_to } : {}) })
      .select().single();
    if (error) throw error;
    await load(true);
    return data;
  }, [org, profile, load]);

  const revokeInvite = useCallback(async (inviteId) => {
    const { error } = await supabase.from('invites').delete().eq('id', inviteId);
    if (error) throw error;
    await load(true);
  }, [load]);

  return { invites, sendInvite, revokeInvite, reload: useCallback(() => load(true), [load]) };
}

// ── Employee availability ─────────────────────────────────────────────────────
export function useAvailability(employeeId) {
  const { org, profile } = useAuth();
  const targetId = employeeId || profile?.id;
  const cKey = targetId ? `avail_emp_${targetId}` : null;
  const [availability, setAvailability] = useState(() => getCache(cKey) ?? []);
  const [loading, setLoading]           = useState(() => getCache(cKey) === undefined);

  const load = useCallback(async (skipCache = false) => {
    if (!targetId) { setLoading(false); return; }
    const key = `avail_emp_${targetId}`;
    if (!skipCache) {
      const cached = getCache(key);
      if (cached !== undefined) { setAvailability(cached); setLoading(false); }
    }
    try {
      const { data } = await supabase.from('availability').select('*').eq('employee_id', targetId);
      const result = data || [];
      setCache(key, result); setAvailability(result);
    } finally { setLoading(false); }
  }, [targetId]);

  useEffect(() => { load(); }, [load]);

  const saveAvailability = useCallback(async (rows) => {
    if (!org) return;
    const upsertRows = rows.map(r => ({ ...r, org_id: org.id, employee_id: targetId, updated_at: new Date().toISOString() }));
    const { error } = await supabase.from('availability').upsert(upsertRows, { onConflict: 'employee_id,day_of_week' });
    if (error) throw error;
    await load(true);
  }, [org, targetId, load]);

  return { availability, loading, saveAvailability };
}

export function useAnalytics() {
  const { org } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!org) { setLoading(false); return; }
    setLoading(true);
    try {
      const now   = new Date();
      const start = toISO(new Date(now.getFullYear(), now.getMonth(), 1));
      const end   = toISO(new Date(now.getFullYear(), now.getMonth() + 1, 0));

      const [shiftsRes, availRes, profilesRes] = await Promise.all([
        supabase.from('shifts')
          .select('id, employee_id, shift_date, duration_hours, position, department')
          .eq('org_id', org.id).gte('shift_date', start).lte('shift_date', end),
        supabase.from('available_shifts')
          .select('id, is_open')
          .eq('org_id', org.id)
          .gte('created_at', start + 'T00:00:00Z').lte('created_at', end + 'T23:59:59Z'),
        // Fetch ALL profiles (any role, active or not) so no one shows as "Unknown"
        supabase.from('profiles')
          .select('id, name, role, avatar_color, avatar_text_color, is_active')
          .eq('org_id', org.id),
      ]);

      const shifts   = shiftsRes.data  || [];
      const avail    = availRes.data   || [];
      const profiles = profilesRes.data || [];

      const empMap = {};
      profiles.forEach(p => { empMap[p.id] = p; });

      // Per-employee stats
      const empStats = {};
      shifts.forEach(s => {
        if (!empStats[s.employee_id]) empStats[s.employee_id] = { shiftCount: 0, totalHours: 0 };
        empStats[s.employee_id].shiftCount += 1;
        empStats[s.employee_id].totalHours += s.duration_hours || 0;
      });

      const employeeBreakdown = Object.entries(empStats).map(([id, stats]) => ({
        id,
        name:        empMap[id]?.name || 'Unknown',
        avatarColor: empMap[id]?.avatar_color,
        avatarText:  empMap[id]?.avatar_text_color,
        shiftCount:  stats.shiftCount,
        totalHours:  Math.round(stats.totalHours * 10) / 10,
      })).sort((a, b) => b.shiftCount - a.shiftCount);

      // Shifts by day of week
      const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
      const dayBuckets = Array(7).fill(0);
      shifts.forEach(s => { dayBuckets[new Date(s.shift_date + 'T12:00:00').getDay()] += 1; });
      const shiftsByDay = DAY_NAMES.map((name, i) => ({ name, shifts: dayBuckets[i] }));

      // Shifts by department
      const deptMap = {};
      shifts.forEach(s => {
        const key = s.department || 'Unassigned';
        deptMap[key] = (deptMap[key] || 0) + 1;
      });
      const shiftsByDept = Object.entries(deptMap)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);

      // Shifts by position
      const posMap = {};
      shifts.forEach(s => {
        const key = s.position || 'Unassigned';
        posMap[key] = (posMap[key] || 0) + 1;
      });
      const shiftsByPosition = Object.entries(posMap)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 6);

      // Coverage
      const postedShifts       = avail.length;
      const filledShifts       = avail.filter(a => !a.is_open).length;
      const fillRate           = postedShifts > 0 ? Math.round((filledShifts / postedShifts) * 100) : 0;
      const activeEmployees    = profiles.filter(p => p.is_active);
      const scheduledEmployees = new Set(shifts.map(s => s.employee_id)).size;
      const coveragePct        = activeEmployees.length > 0
        ? Math.min(100, Math.round((scheduledEmployees / activeEmployees.length) * 100)) : 0;
      const avgHoursPerEmp     = employeeBreakdown.length > 0
        ? Math.round((employeeBreakdown.reduce((s, e) => s + e.totalHours, 0) / employeeBreakdown.length) * 10) / 10 : 0;

      setData({
        monthLabel: now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        totalShifts: shifts.length,
        totalHours:  Math.round(shifts.reduce((s, x) => s + (x.duration_hours || 0), 0) * 10) / 10,
        avgHoursPerEmp, coveragePct, fillRate, postedShifts, filledShifts,
        employeeBreakdown, shiftsByDay, shiftsByDept, shiftsByPosition,
      });
    } finally { setLoading(false); }
  }, [org]);

  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

export function useScheduleHistory() {
  const { org } = useAuth();
  const cKey = org ? `hist_${org.id}` : null;
  const [schedules, setSchedules] = useState(() => getCache(cKey) ?? []);
  const [loading, setLoading]     = useState(() => getCache(cKey) === undefined);

  const load = useCallback(async (skipCache = false) => {
    if (!org) { setLoading(false); return; }
    const key = `hist_${org.id}`;
    if (!skipCache) {
      const cached = getCache(key);
      if (cached !== undefined) { setSchedules(cached); setLoading(false); }
    }
    try {
      const { data } = await supabase
        .from('schedules')
        .select('id, week_start, published_at')
        .eq('org_id', org.id).eq('published', true)
        .order('week_start', { ascending: false });
      const result = data || [];
      setCache(key, result);
      setSchedules(result);
    } finally { setLoading(false); }
  }, [org]);

  useEffect(() => { load(); }, [load]);
  return { schedules, loading, reload: useCallback(() => load(true), [load]) };
}
